import {
  forwardRef,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
} from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { useReducedMotion } from "motion/react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import {
  isFormattedBonusPrizeTitle,
  isFormattedCashPrizeTitle,
} from "@/utils/helpers";
import { splitCurrencySymbol } from "@/utils/format-money";

import { PrizeMark, type PrizeMarkKind } from "./prize-mark";
import { SpinningAmount } from "./spinning-amount";

/**
 * THE prize component of the dashboard — every prize, from a jackpot's hero pot to a leaderboard
 * cell, is set through this one so the signature is decided in one place: Plus Jakarta on the
 * heading face, the metallic gold gradient clipped to the glyphs (`.text-prize-gold`), and an
 * embossing shadow that scales with the size. The Figma `prize` text form, extended with the sizes
 * the dashboard actually needs.
 *
 * - `size`: the ramp, in px — hero 52 · xl 48 · large 40 · display 36 · title 32 · medium 24 ·
 *   base 20 · body 18 · small 14 · xs 12 — plus `inherit`, which sets no size or leading and takes
 *   the surrounding type (table cells, inline copy).
 * - `weight`: ExtraBold is the signature; step down where a heavy face would shout (rows, chips).
 * - `tone`: `gold` is the gradient; `flat` the plain prize colour for quiet contexts (a muted
 *   leaderboard field); `muted` the frozen figure of something finished — no gold, no depth;
 *   `gray` the gold's own gradient in greyscale, a step darker — still a prize, quieter (the
 *   places beside yours on the launcher's leaderboard wheel); its mark goes grey with it.
 * - `shadow`: the emboss; on by default from `body` up, off below (it smears at row sizes) — pass
 *   it to override either way.
 * - `icon`: the prize mark on the leading side of the label, sized in `em` so it scales with the
 *   type — `"star"` today (`true` is shorthand for it); the slot is there for a mark per campaign
 *   type later. A MONEY prize — cash ("€100 Cash", "$25" — `isFormattedCashPrizeTitle`) or bonus
 *   ("€10 Bonus" — `isFormattedBonusPrizeTitle`) — puts its currency symbol in that slot instead
 *   — same box, same place — and the label carries on without it; every other prize (catalog
 *   item, free spins) keeps the star, even when its title leads with "€" ("€25,000 Cash +
 *   Weekend…"). `currencyMark={false}` forces
 *   the star for a catalog item whose whole title happens to be an amount (`is_catalog_item`). The
 *   wrapper turns into an inline flex row; pass `flex` in `className` where a block-level row is
 *   wanted.
 * - `fit`: what a prize does when it is wider than its box — never truncate (widget CLAUDE.md).
 *   Both modes first SHRINK the type to fit one line, down to `minScale` (75%) of its size;
 *   past that floor `"wrap"` breaks onto more lines (anywhere with height to give) and
 *   `"marquee"` stays on one line and glides over the hidden part (rows with one line to give)
 *   — only the words glide, the mark stays put. The glide rests at both ends, pauses under the
 *   pointer, stops at the start after a few passes (WCAG 2.2.2), and becomes a wrap under
 *   `prefers-reduced-motion`. Unset: no measuring, the plain behaviour above. The box renders
 *   as a block; size it from `className`.
 * - `animate`: the digits ride slot-machine reels (`SpinningAmount`: idle spins, an immediate spin
 *   on change, a lock-in flare). Jackpot pots only — nothing else in the dashboard moves on its
 *   own — and only for a plain string child. Silently static under `prefers-reduced-motion`.
 *
 * The depth is a `drop-shadow` filter, not `text-shadow`: a text shadow paints over a background
 * that is clipped to the text and would cover the gradient. Variants are not exported on purpose
 * (react-refresh wants component-only modules).
 */
const prizeTextVariants = cva("font-heading", {
  variants: {
    // Size before leading in each string: twMerge lets a font-size class override an earlier
    // `leading-*`, so the other order would silently drop the 1.12.
    // Phones get the scale one step down (a 52px figure on a 375px screen is a poster, not a
    // number); `lg:` restores the desktop scale. Row sizes (body and below) step down too.
    size: {
      hero: "text-[40px] leading-[1.12] lg:text-[52px]",
      xl: "text-[36px] leading-[1.12] lg:text-[48px]",
      large: "text-[32px] leading-[1.12] lg:text-[40px]",
      display: "text-[28px] leading-[1.12] lg:text-[36px]",
      title: "text-[26px] leading-[1.12] lg:text-[32px]",
      medium: "text-[20px] leading-[1.12] lg:text-[24px]",
      base: "text-[20px] leading-[1.2]",
      body: "text-[15px] leading-5 lg:text-[18px] lg:leading-6",
      small: "text-[12px] leading-4 lg:text-[14px] lg:leading-5",
      xs: "text-[11px] leading-[14px] lg:text-[12px] lg:leading-4",
      inherit: "",
    },
    weight: {
      extrabold: "font-extrabold",
      bold: "font-bold",
      semibold: "font-semibold",
      medium: "font-medium",
    },
    tone: {
      gold: "text-prize-gold",
      flat: "text-prizes",
      muted: "text-muted-foreground",
      /** The prize gradient in greyscale, a step down: a prize that is not the one in focus. */
      gray: "text-prize-gray",
      /** On a gold surface (IGB-MAT-02): the ink token for type and mark, a 1px light emboss. */
      onGold: "text-prize-ink",
      /** Not a prize — a line kept whole the way a prize is (it shrinks, then glides), in the
       *  foreground ink: the campaign's title over its Opt In on the in-game button. */
      plain: "text-foreground",
      // The surrounding text colour: a prize on a coloured surface (the gold win toast), where
      // gold-on-gold would vanish.
      inherit: "",
    },
  },
  defaultVariants: { size: "medium", weight: "extrabold", tone: "gold" },
});

export type PrizeTextSize = NonNullable<
  VariantProps<typeof prizeTextVariants>["size"]
>;

/** The emboss per size: deeper the bigger the type; none at row sizes unless asked for. The
 *  colour is `--prize-shadow` — the prize token pushed deep and warm, never black. The filter
 *  itself is the `.prize-emboss` utility: Tailwind won't compile an arbitrary `drop-shadow-[…]`
 *  whose colour is a `var()`, so the size only sets the offset/blur it reads. */
const emboss = (y: number, blur: number) =>
  `prize-emboss [--emboss-y:${y}px] [--emboss-blur:${blur}px]`;
const SHADOW: Record<PrizeTextSize, string | null> = {
  hero: emboss(5, 5),
  xl: emboss(4, 4),
  large: emboss(3, 3),
  display: emboss(3, 3),
  title: emboss(2, 3),
  medium: emboss(2, 2),
  base: emboss(1, 2),
  body: emboss(1, 2),
  small: null,
  xs: null,
  inherit: null,
};
/** The shadow used when `shadow` is forced on at a size that has none by default. */
const SHADOW_FORCED = emboss(1, 1);

/** How far `fit` shrinks a prize before it wraps / glides — below this it stops standing out. */
export const PRIZE_FIT_MIN_SCALE = 0.75;
/** Glide pace: slow enough to read while it moves (the overlay `Marquee`'s speed). */
const MARQUEE_SPEED_PX_PER_S = 24;
/** Floor for one pass, so a barely-overflowing prize still rests readably at each end. */
const MARQUEE_MIN_PASS_S = 3;

type FitLayout = { scale: number; overflow: number };

export interface PrizeTextProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof prizeTextVariants> {
  /** Override the size's default emboss (on from `body` up, off below). */
  shadow?: boolean;
  /** Slot-machine reels on the digits — jackpot pots only. Needs a plain string child. */
  animate?: boolean;
  /** The prize mark before the label, scaling with the type. `true` = the star. */
  icon?: boolean | PrizeMarkKind;
  /** Let a cash prize's currency symbol take the mark's slot (default). `false` for catalog
   *  items — always the star, even for a free-text title that reads as a bare amount. */
  currencyMark?: boolean;
  /** Shrink-then-wrap or shrink-then-glide when the prize is wider than its box. With
   *  `animate`, the reels only shrink — a spinning amount can neither glide nor wrap. */
  fit?: "wrap" | "marquee";
  /** The smallest scale `fit` shrinks to before wrapping / gliding. */
  minScale?: number;
}

export const PrizeText = forwardRef<HTMLSpanElement, PrizeTextProps>(
  (
    {
      size,
      weight,
      tone,
      shadow,
      animate = false,
      icon = false,
      currencyMark = true,
      fit,
      minScale = PRIZE_FIT_MIN_SCALE,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const reducedMotion = useReducedMotion();
    const { t } = useTranslation();
    const boxRef = useRef<HTMLSpanElement | null>(null);
    const sizerRef = useRef<HTMLSpanElement | null>(null);
    const [layout, setLayout] = useState<FitLayout>({ scale: 1, overflow: 0 });
    const setRefs = useCallback(
      (node: HTMLSpanElement | null) => {
        boxRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      },
      [ref]
    );
    const resolvedSize: PrizeTextSize = size ?? "medium";
    const resolvedTone = tone ?? "gold";
    const wantsShadow = shadow ?? SHADOW[resolvedSize] !== null;
    // On gold the emboss is a 1px light edge under the dark type — the surface's own
    // highlight, the way a stamped coin reads — at every size unless switched off.
    const shadowClass =
      resolvedTone === "gold" && wantsShadow
        ? (SHADOW[resolvedSize] ?? SHADOW_FORCED)
        : resolvedTone === "onGold" && shadow !== false
          ? "prize-emboss-on-gold"
          : null;
    const markTone = resolvedTone === "onGold" ? "ink" : "gold";
    // A grey prize's star goes grey with it (the gold mark, desaturated).
    const markGray = resolvedTone === "gray" ? "prize-mark-gray" : null;
    const requestedMark: PrizeMarkKind | null =
      icon === true ? "star" : icon || null;
    // The currency symbol is for money prizes — cash and bonus ("€100 Cash", "€10 Bonus");
    // anything else that merely starts with one (a catalog "€25,000 Cash + Weekend…") keeps the
    // star.
    const isCash =
      requestedMark !== null &&
      currencyMark &&
      typeof children === "string" &&
      (isFormattedCashPrizeTitle(children, t("prizes.cash", "Cash")) ||
        isFormattedBonusPrizeTitle(children, t("prizes.bonus", "Bonus")));
    const cash = isCash ? splitCurrencySymbol(children as string) : null;
    // Cash never wears the star: a cash label with a glyph puts the glyph in the slot; one whose
    // currency is a word ("519.40 kr", "SEK 500") shows no mark at all.
    const mark: PrizeMarkKind | null = isCash && !cash ? null : requestedMark;
    const spins =
      animate &&
      !reducedMotion &&
      resolvedTone === "gold" &&
      typeof children === "string";
    const fitting = fit != null;

    useLayoutEffect(() => {
      const box = boxRef.current;
      const sizer = sizerRef.current;
      if (!fitting || !box || !sizer) return;
      // Layout widths (offset/client), not getBoundingClientRect: an ancestor mid-transform (a
      // dialog scaling in) would otherwise read as a narrower box and shrink the prize for good.
      const measure = () => {
        const natural = sizer.offsetWidth;
        const available = box.clientWidth;
        if (!natural || !available) return;
        const ratio = available / natural;
        const scale = Math.min(1, Math.max(minScale, ratio));
        const overflow =
          ratio < minScale ? Math.ceil(natural * minScale - available) : 0;
        setLayout((prev) =>
          prev.scale === scale && prev.overflow === overflow
            ? prev
            : { scale, overflow }
        );
      };
      measure();
      if (typeof ResizeObserver !== "function") return;
      // The box resizing and a late webfont (the sizer) both change the answer.
      const observer = new ResizeObserver(measure);
      observer.observe(box);
      observer.observe(sizer);
      return () => observer.disconnect();
    }, [fitting, minScale, children]);

    // The currency symbol is a glyph, not a box: no fixed slot and no added space (its side bearing
    // is the gap, as in running text: "€50", never "€ 50"), and it always leads the amount — the
    // widget's one convention, whatever the locale — like the star.
    const markNode =
      mark &&
      (cash ? (
        <span
          aria-hidden
          className="flex h-[0.9em] shrink-0 items-center text-[0.9em] leading-none font-extrabold"
        >
          {cash.symbol}
        </span>
      ) : (
        <PrizeMark
          mark={mark}
          tone={markTone}
          className={cn("size-[0.9em] shrink-0", markGray)}
        />
      ));
    const label: ReactNode = cash ? cash.rest : children;
    const markGap = cash ? "gap-0" : "gap-[0.28em]";
    // In the fitted row the tone sits on the mark itself (the gradient clipped to the moving
    // label does not reach it).
    const fittedMark =
      mark &&
      (cash ? (
        <span
          aria-hidden
          className={cn(
            prizeTextVariants({ size: null, weight: null, tone }),
            "flex h-[0.9em] shrink-0 items-center text-[0.9em] leading-none font-extrabold"
          )}
        >
          {cash.symbol}
        </span>
      ) : (
        <PrizeMark
          mark={mark}
          tone={markTone}
          className={cn("size-[0.9em] shrink-0", markGray)}
        />
      ));

    if (fitting) {
      const overflowing = layout.overflow > 0;
      const moving =
        fit === "marquee" && overflowing && !reducedMotion && !spins;
      const wrapping = overflowing && !moving && !spins;
      const travel = layout.overflow / MARQUEE_SPEED_PX_PER_S;
      const lineStyle: CSSProperties & Record<`--${string}`, string> = {};
      if (moving) {
        // The keyframes park the text for the first and last 15% of a pass: glide = 70%.
        lineStyle["--marquee-distance"] = `-${layout.overflow}px`;
        lineStyle["--marquee-duration"] =
          `${Math.max(travel / 0.7, MARQUEE_MIN_PASS_S)}s`;
      }
      return (
        <span
          ref={setRefs}
          className={cn(
            prizeTextVariants({ size, weight, tone: null }),
            // A column, not a block: a block's line box is the FULL size's,
            // and a shrunk line rode at its top — high in whatever centres
            // the prize (Sebastian, 2026-10-16: "the prize is not
            // vertically centred"). A column has no line box of its own; the
            // sizer is zero-tall and the line below as tall as its own.
            "relative flex max-w-full min-w-0 flex-col",
            // A text leaf the in-game button turns back upright on its
            // mirrored side (`igw-button.css`); no effect anywhere else.
            "upright",
            moving && "prize-fit text-left",
            className
          )}
          aria-label={cash ? String(children) : props["aria-label"]}
          {...props}
        >
          {/* The one-line natural width at full size: measured by `fit`, and it holds the box at
              min(natural, room) whatever the current scale — so shrinking can't feed back into
              the width it was measured against. Zero height, never painted or read. */}
          <span
            aria-hidden
            className="invisible block h-0 overflow-hidden whitespace-nowrap"
          >
            <span
              ref={sizerRef}
              className={cn(mark && cn("inline-flex items-center", markGap))}
            >
              {mark && markNode}
              {label}
            </span>
          </span>
          {/* The line at the fitted size: a block as tall as its own (scaled) line, so the
              prize centres wherever it is centred, and it inherits text-align, so the row inside
              still sits left / centred / right as its context says (a stretched flex row would
              not — 👺 Belmont, N1). */}
          <span
            className="block max-w-full"
            style={{ fontSize: `${layout.scale}em` }}
          >
            {/* The row scales as one (mark included); only the label behind the mark moves — the
              mark stays put as the prize's anchor while the words glide past it. */}
            <span
              className={cn(
                "inline-flex max-w-full items-center align-top",
                markGap,
                shadowClass
              )}
            >
              {mark && fittedMark}
              <span className={cn("min-w-0", moving && "overflow-x-clip")}>
                {/* Tone sits on the moving line, not an ancestor: a gradient clipped to text does
                  not follow a transformed child, so the gliding glyphs would lose their gold. */}
                {/* Reels are never re-keyed: a remount would swallow the spin a new value starts. */}
                <span
                  key={
                    typeof children === "string" && !spins
                      ? children
                      : undefined
                  }
                  className={cn(
                    prizeTextVariants({ size: null, weight: null, tone }),
                    "inline-block align-top",
                    wrapping
                      ? "break-words whitespace-normal"
                      : "whitespace-nowrap",
                    moving && "prize-marquee"
                  )}
                  style={lineStyle}
                >
                  {spins ? <SpinningAmount display={children} /> : label}
                </span>
              </span>
            </span>
          </span>
        </span>
      );
    }

    return (
      <span
        ref={ref}
        className={cn(
          prizeTextVariants({ size, weight, tone }),
          shadowClass,
          mark && cn("inline-flex items-center", markGap),
          "upright",
          className
        )}
        // The cash label is split visually (symbol in the mark's slot, figure after it); the
        // whole string is read as one — via `aria-label`, never an `sr-only` copy: inside a
        // text-clipped element a hidden glyph still paints through the mask.
        aria-label={cash ? String(children) : props["aria-label"]}
        {...props}
      >
        {markNode}
        {spins ? (
          <SpinningAmount display={children} />
        ) : mark ? (
          <span className="min-w-0 break-words">{label}</span>
        ) : (
          children
        )}
      </span>
    );
  }
);
PrizeText.displayName = "PrizeText";
